import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogActions,
  Box, Typography, Button, Chip, Grid, Paper,
  CircularProgress, Alert, Divider, LinearProgress, Avatar,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Person as PersonIcon,
  AccountBalance as LoanIcon,
  Groups as GroupIcon,
  History as HistoryIcon,
  Shield as ShieldIcon,
  Info as InfoIcon,
  Security as SecurityIcon,
  VerifiedUser as GuarantorIcon,
} from '@mui/icons-material';
import { fetchLoanApprovalAssessment } from '../Users/api';

// ─── Palette ─────────────────────────────────────────────────────────────────
const P = {
  navy:    '#0f2744',
  blue:    '#1a56a0',
  teal:    '#0d9488',
  amber:   '#d97706',
  red:     '#dc2626',
  green:   '#16a34a',
  purple:  '#7c3aed',
  surface: '#f8fafc',
  border:  '#e2e8f0',
  muted:   '#64748b',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt    = (n) => `KSh ${parseFloat(n || 0).toLocaleString()}`;
const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

// Convert raw score + max → clean percentage string e.g. "100%" or "85.7%"
const scorePct = (score, max) => {
  if (!max) return '—';
  const p = (parseFloat(score) / max) * 100;
  return `${Number.isInteger(p) ? p : p.toFixed(1)}%`;
};

const behaviorColor = (b) => ({
  excellent: P.green, good: P.teal, fair: P.amber, poor: P.red, no_history: P.muted,
}[b] || P.muted);

const gradeColor = (g) => {
  if (!g || g === 'N/A') return P.muted;
  if (g.startsWith('A'))  return P.green;
  if (g.startsWith('B'))  return P.teal;
  if (g.startsWith('C'))  return P.amber;
  return P.red;
};

const riskColor = (level) => ({
  low: P.green, moderate: P.teal, elevated: P.amber, high: P.red,
}[level] || P.muted);

// ─── Atoms ───────────────────────────────────────────────────────────────────
const SectionTitle = ({ icon: Icon, label, color = P.blue }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
    <Icon sx={{ color, fontSize: 20 }} />
    <Typography sx={{
      fontWeight: 700, color, letterSpacing: '0.04em',
      fontFamily: '"Barlow Condensed", sans-serif',
      textTransform: 'uppercase', fontSize: '0.82rem',
    }}>
      {label}
    </Typography>
  </Box>
);

const KV = ({ label, value, valueColor }) => (
  <Box sx={{ mb: 1.2 }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, color: valueColor || 'text.primary' }}>{value}</Typography>
  </Box>
);

// ScoreBar now shows percentage instead of raw score/max
const ScoreBar = ({ label, score, max, color }) => {
  const pct = Math.min((score / max) * 100, 100);
  return (
    <Box sx={{ mb: 1.4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color }}>{scorePct(score, max)}</Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct} sx={{
        height: 6, borderRadius: 3, bgcolor: 'action.selected',
        '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
      }} />
    </Box>
  );
};

const GradeBadge = ({ grade, score }) => (
  <Box sx={{
    textAlign: 'center',
    background: `${gradeColor(grade)}15`,
    border: `1.5px solid ${gradeColor(grade)}40`,
    borderRadius: 2, px: 2, py: 1.2, minWidth: 64,
  }}>
    <Typography variant="h4" sx={{
      fontWeight: 800, color: gradeColor(grade), lineHeight: 1,
      fontFamily: '"Barlow Condensed", sans-serif',
    }}>{grade}</Typography>
    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>
      {score?.toFixed(1)}/100
    </Typography>
  </Box>
);

const BehaviorPill = ({ value }) => (
  <Chip
    label={(value || 'N/A').replace('_', ' ').toUpperCase()}
    size="small"
    sx={{
      bgcolor: `${behaviorColor(value)}18`,
      color: behaviorColor(value),
      border: `1px solid ${behaviorColor(value)}40`,
      fontWeight: 700, fontSize: '0.7rem', letterSpacing: '0.06em',
    }}
  />
);

const TabPanel = ({ children, value, index }) =>
  value === index ? <Box sx={{ pt: 2.5 }}>{children}</Box> : null;

// ─── Overview Tab ─────────────────────────────────────────────────────────────
const OverviewTab = ({ data }) => {
  const { member, loan_application: loan } = data;
  const guarantors = loan?.guarantors || [];
  const security   = loan?.security_details;

  return (
    <Grid container spacing={2.5}>

      {/* ── Member card ── */}
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
          <SectionTitle icon={PersonIcon} label="Member" color={P.blue} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar sx={{ bgcolor: P.blue, width: 48, height: 48, fontSize: '1.3rem', fontWeight: 700 }}>
              {member?.name?.charAt(0)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{member?.name}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{member?.member_id}</Typography>
            </Box>
            <Chip
              label={member?.is_active ? 'Active' : 'Inactive'} size="small"
              sx={{
                bgcolor: member?.is_active ? `${P.green}18` : `${P.red}18`,
                color:   member?.is_active ? P.green : P.red,
                fontWeight: 700, fontSize: '0.7rem',
              }}
            />
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={1}>
            <Grid item xs={6}><KV label="Group"        value={member?.group_name} /></Grid>
            <Grid item xs={6}><KV label="Phone"        value={member?.phone || '—'} /></Grid>
            <Grid item xs={6}><KV label="Occupation"   value={member?.occupation || '—'} /></Grid>
            <Grid item xs={6}>
              <KV label="Savings Balance" value={fmt(member?.savings_balance)} valueColor={P.green} />
            </Grid>
            <Grid item xs={6}>
              <KV
                label="Loan Balance"
                value={fmt(member?.loan_balance)}
                valueColor={member?.loan_balance > 0 ? P.amber : P.green}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* ── Loan application card ── */}
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
          <SectionTitle icon={LoanIcon} label="Loan Application" color={P.teal} />
          {loan ? (
            <>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={loan.status?.toUpperCase()} size="small"
                  sx={{
                    bgcolor: loan.status === 'pending' ? `${P.amber}18` : `${P.blue}18`,
                    color:   loan.status === 'pending' ? P.amber : P.blue,
                    fontWeight: 700, fontSize: '0.7rem',
                  }}
                />
                <Chip
                  label={loan.display_id || `#${loan.loan_id}`} size="small"
                  sx={{ bgcolor: 'action.hover', color: 'text.secondary', fontSize: '0.7rem' }}
                />
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={6}><KV label="Principal"       value={fmt(loan.amount)}            valueColor="primary.main" /></Grid>
                <Grid item xs={6}><KV label="Total Repayment" value={fmt(loan.total_repayment)} /></Grid>
                <Grid item xs={6}><KV label="Installments"    value={`${loan.installments} months`} /></Grid>
                <Grid item xs={6}><KV label="Monthly Payment" value={fmt(loan.installment_amount)} /></Grid>
                <Grid item xs={6}><KV label="Applied"         value={fmtDate(loan.applied_date)} /></Grid>
                <Grid item xs={6}><KV label="Officer"         value={loan.officer_name || '—'} /></Grid>
                {loan.net_disbursement && (
                  <Grid item xs={6}>
                    <KV label="Net Disbursement" value={fmt(loan.net_disbursement)} valueColor={P.green} />
                  </Grid>
                )}
              </Grid>
              {/* Deduction summary if finalized */}
              {loan.deduction_breakdown && loan.deductions_finalized && (
                <Box sx={{ mt: 1.5, p: 1.2, borderRadius: 1.5, bgcolor: `${P.amber}08`, border: `1px solid ${P.amber}25` }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: P.amber, display: 'block', mb: 0.5 }}>
                    Deductions — KSh {loan.deduction_breakdown.total_deductions?.toLocaleString()}
                  </Typography>
                  {loan.deduction_breakdown.processing_fee > 0 && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      Processing fee: {fmt(loan.deduction_breakdown.processing_fee)}
                    </Typography>
                  )}
                  {loan.deduction_breakdown.advocate_fee > 0 && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      Advocate fee: {fmt(loan.deduction_breakdown.advocate_fee)}
                    </Typography>
                  )}
                  {loan.deduction_breakdown.advance_deducted > 0 && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      Advance deducted: {fmt(loan.deduction_breakdown.advance_deducted)}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          ) : (
            <Alert severity="info" sx={{ mt: 1 }}>No pending loan application found.</Alert>
          )}
        </Paper>
      </Grid>

      {/* ── Guarantors ── */}
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <SectionTitle icon={GuarantorIcon} label={`Guarantors (${guarantors.length})`} color={P.purple} />
          {guarantors.length === 0 ? (
            <Box sx={{ p: 2, borderRadius: 1.5, bgcolor: 'action.hover', border: 1, borderColor: 'divider', borderStyle: 'dashed', textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>No guarantors recorded for this loan.</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {guarantors.map((g, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Box sx={{
                    p: 2, borderRadius: 2,
                    border: `1.5px solid ${P.purple}30`,
                    bgcolor: `${P.purple}06`,
                    display: 'flex', gap: 1.5, alignItems: 'flex-start',
                  }}>
                    {/* Avatar with number so officers know order */}
                    <Avatar sx={{
                      bgcolor: P.purple, width: 36, height: 36,
                      fontSize: '0.85rem', fontWeight: 800, flexShrink: 0,
                    }}>
                      {i + 1}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.3 }}>
                        {g.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        {g.member_id} · {g.phone || '—'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1.5, mt: 0.8, flexWrap: 'wrap' }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.65rem' }}>
                            Savings
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: P.green }}>
                            {fmt(g.savings_balance)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.65rem' }}>
                            Loan Balance
                          </Typography>
                          <Typography variant="caption" sx={{
                            fontWeight: 700,
                            color: g.loan_balance > 0 ? P.amber : P.green,
                          }}>
                            {fmt(g.loan_balance)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </Grid>

      {/* ── Security / Collateral ── */}
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <SectionTitle icon={SecurityIcon} label="Security / Collateral" color={P.teal} />
          {security ? (
            <Box sx={{
              p: 2, borderRadius: 1.5,
              bgcolor: `${P.teal}06`,
              border: `1.5px solid ${P.teal}25`,
            }}>
              <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {security}
              </Typography>
            </Box>
          ) : (
            <Box sx={{
              p: 2, borderRadius: 1.5, bgcolor: 'action.hover',
              border: 1, borderColor: 'divider', borderStyle: 'dashed', textAlign: 'center',
            }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No security details recorded for this loan.
              </Typography>
            </Box>
          )}
        </Paper>
      </Grid>

    </Grid>
  );
};

// ─── Performance History Tab ──────────────────────────────────────────────────
const PerformanceTab = ({ data }) => {
  const ph = data?.performance_history;
  if (!ph?.has_history) {
    return (
      <Alert severity="info">
        {ph?.message || 'No performance history available from November 2025 onwards.'}
      </Alert>
    );
  }
  return (
    <Box>
      {/* Info banner — only show period end (meeting date), not range */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2, mb: 3,
        p: 2, bgcolor: 'background.paper', borderRadius: 2, border: 1, borderColor: 'divider',
      }}>
        <InfoIcon sx={{ color: P.blue, flexShrink: 0 }} />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Showing last <strong>{ph.history_count}</strong> closed periods ·{' '}
          {fmtDate(ph.earliest_date)} – {fmtDate(ph.latest_date)}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {ph.performance_history.map((period, idx) => {
          const s = period.score_breakdown;
          const isLatest = idx === 0;
          return (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Paper variant="outlined" sx={{
                p: 2, borderRadius: 2, height: '100%',
                borderColor: isLatest ? P.blue : 'divider',
                borderWidth: isLatest ? 2 : 1,
              }}>
                {isLatest && (
                  <Chip label="Latest" size="small" sx={{
                    mb: 1, bgcolor: `${P.blue}18`, color: P.blue,
                    fontWeight: 700, fontSize: '0.7rem',
                  }} />
                )}

                {/* Header: meeting date + grade */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    {/* Just the meeting date (period_end), not the range */}
                    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      {fmtDate(period.period_end)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Meeting date
                    </Typography>
                  </Box>
                  <GradeBadge grade={period.grade} score={period.total_score} />
                </Box>

                <Divider sx={{ mb: 1.5 }} />

                {/* Score bars — percentage format */}
                <ScoreBar label="Loan Repayment"    score={s.loan_repayment.score}    max={35} color={gradeColor(period.grade)} />
                <ScoreBar label="Advance Interest"  score={s.advance_interest.score}  max={25} color={P.teal} />
                <ScoreBar label="Contribution"      score={s.contribution.score}      max={15} color={P.blue} />
                <ScoreBar label="Min Savings"       score={s.minimum_savings.score}   max={15} color={P.purple} />
                <ScoreBar
                  label="Defaulter Score"
                  score={s.defaulter.score} max={10}
                  color={s.defaulter.score < 5 ? P.red : P.green}
                />

                {/* Defaulter status pill */}
                <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Defaulter status:</Typography>
                  <Chip
                    label={period.defaulter_status} size="small"
                    sx={{
                      fontSize: '0.65rem', fontWeight: 700,
                      bgcolor: period.defaulter_status === 'YES' ? `${P.red}18` : `${P.green}18`,
                      color:   period.defaulter_status === 'YES' ? P.red : P.green,
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

// ─── Repayment History Tab ────────────────────────────────────────────────────
const RepaymentTab = ({ data }) => {
  const rh = data?.repayment_history;
  if (!rh || rh.error) {
    return <Alert severity="error">{rh?.error || 'Could not load repayment history.'}</Alert>;
  }
  const { loan_history: lh, advance_history: ah, overall_assessment: oa } = rh;

  return (
    <Box>
      {/* Overall banner */}
      <Paper sx={{
        p: 2, mb: 3, borderRadius: 2,
        background: `linear-gradient(135deg, ${P.navy}f0, ${P.blue}e0)`,
        color: 'white',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="overline" sx={{ opacity: 0.7, fontSize: '0.65rem' }}>
              Overall Creditworthiness
            </Typography>
            <Typography variant="h5" sx={{
              fontWeight: 800, fontFamily: '"Barlow Condensed", sans-serif', textTransform: 'uppercase',
            }}>
              {oa?.creditworthiness?.replace('_', ' ') || '—'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>{oa?.recommendation}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>Loans</Typography>
              <BehaviorPill value={oa?.loan_behavior} />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>Advances</Typography>
              <BehaviorPill value={oa?.advance_behavior} />
            </Box>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={2.5}>
        {/* Loan History */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <SectionTitle icon={LoanIcon} label="Loan Repayment History" color={P.blue} />
            {lh?.has_loan_history ? (
              <>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  {[
                    { label: 'On Time', count: lh.repayment_summary?.on_time, color: P.green },
                    { label: 'Early',   count: lh.repayment_summary?.early,   color: P.teal },
                    { label: 'Late',    count: lh.repayment_summary?.late,     color: P.red },
                  ].map(({ label, count, color }) => (
                    <Box key={label} sx={{
                      textAlign: 'center', px: 1.5, py: 0.8,
                      borderRadius: 1.5, bgcolor: `${color}12`, border: `1px solid ${color}30`,
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, color, lineHeight: 1 }}>{count}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
                    </Box>
                  ))}
                </Box>
                {lh.repaid_loans?.length > 0 && (
                  <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                    {lh.repaid_loans.map((loan, i) => (
                      <Box key={i} sx={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        p: 1, mb: 0.8, borderRadius: 1, bgcolor: 'background.paper', border: 1, borderColor: 'divider',
                      }}>
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>{fmt(loan.amount)}</Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                            {fmtDate(loan.repaid_date)}
                          </Typography>
                        </Box>
                        <Chip
                          label={loan.timeline_status || loan.status} size="small"
                          sx={{
                            fontSize: '0.65rem', fontWeight: 700,
                            color: loan.timeline_status === 'on_time' ? P.green
                              : loan.timeline_status === 'early' ? P.teal
                              : loan.timeline_status === 'late'  ? P.red : P.muted,
                            bgcolor: loan.timeline_status === 'on_time' ? `${P.green}18`
                              : loan.timeline_status === 'early' ? `${P.teal}18`
                              : loan.timeline_status === 'late'  ? `${P.red}18` : `${P.muted}18`,
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
                {lh.active_loan && (
                  <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: `${P.blue}08`, border: `1px solid ${P.blue}30` }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: P.blue, display: 'block', mb: 0.5 }}>
                      Active Loan
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{lh.active_loan.status_detail}</Typography>
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                      <Typography variant="caption">Balance: <strong>{fmt(lh.active_loan.current_balance)}</strong></Typography>
                      <BehaviorPill value={
                        lh.active_loan.timeline_status === 'ahead'   ? 'excellent'
                        : lh.active_loan.timeline_status === 'on_time' ? 'good'
                        : lh.active_loan.timeline_status === 'behind'  ? 'poor' : 'fair'
                      } />
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>No loan history on record.</Typography>
            )}
          </Paper>
        </Grid>

        {/* Advance History */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <SectionTitle icon={HistoryIcon} label="Advance Loan History" color={P.teal} />
            {(() => {
              const reg = ah?.regular_advances;
              if (!reg?.has_history) {
                return <Typography variant="body2" sx={{ color: 'text.secondary' }}>No advance loan history.</Typography>;
              }
              return (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Avg Compliance Rate</Typography>
                      <Typography variant="h5" sx={{
                        fontWeight: 800, color: behaviorColor(reg.payment_behavior), lineHeight: 1.1,
                      }}>
                        {reg.average_compliance_rate}%
                      </Typography>
                    </Box>
                    <BehaviorPill value={reg.payment_behavior} />
                  </Box>
                  {reg.excluded_from_compliance > 0 && (
                    <Alert severity="warning" sx={{ mb: 1.5, py: 0.5, fontSize: '0.75rem' }}>
                      {reg.excluded_from_compliance} advance(s) excluded (missing repaid date)
                    </Alert>
                  )}
                  <Box sx={{ maxHeight: 180, overflowY: 'auto' }}>
                    {reg.repaid_advances?.map((adv, i) => (
                      <Box key={i} sx={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        p: 1, mb: 0.8, borderRadius: 1, bgcolor: 'background.paper', border: 1, borderColor: 'divider',
                        opacity: adv.compliance_excluded ? 0.5 : 1,
                      }}>
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>{fmt(adv.principal_amount)}</Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                            {fmtDate(adv.disbursed_date)} → {adv.repaid_date ? fmtDate(adv.repaid_date) : '?'}
                          </Typography>
                        </Box>
                        {adv.compliance_excluded
                          ? <Chip label="EXCLUDED" size="small" sx={{ fontSize: '0.65rem', bgcolor: `${P.muted}18`, color: P.muted }} />
                          : <Chip label={`${adv.compliance_rate}%`} size="small" sx={{
                              fontSize: '0.65rem', fontWeight: 700,
                              bgcolor: `${behaviorColor(adv.payment_behavior)}18`,
                              color: behaviorColor(adv.payment_behavior),
                            }} />
                        }
                      </Box>
                    ))}
                  </Box>
                  {reg.active_advance && (
                    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: `${P.teal}08`, border: `1px solid ${P.teal}30` }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: P.teal, display: 'block', mb: 0.3 }}>
                        Active Advance
                      </Typography>
                      <Typography variant="caption">
                        Compliance so far: <strong>{reg.active_advance.compliance_rate}%</strong>
                      </Typography>
                    </Box>
                  )}
                </>
              );
            })()}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

// ─── Group Context Tab ────────────────────────────────────────────────────────
const GroupTab = ({ data }) => {
  const gc = data?.group_context;
  if (!gc || gc.error) {
    return <Alert severity="error">{gc?.error || 'Could not load group context.'}</Alert>;
  }
  const { loan_portfolio: lp, risk_score: rs, rating_history: rh } = gc;

  return (
    <Box>
      {/* Group header */}
      <Box sx={{
        mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 2,
        border: 1, borderColor: 'divider', display: 'flex', gap: 2, alignItems: 'center',
      }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 42, height: 42 }}><GroupIcon /></Avatar>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{gc.group_name}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{gc.group_location}</Typography>
        </Box>
        <Box sx={{ ml: 'auto', textAlign: 'right' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Total Members</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main', lineHeight: 1 }}>
            {lp?.total_members}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2.5}>

        {/* Risk Score */}
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
            <SectionTitle icon={ShieldIcon} label="Group Risk Score" color={riskColor(rs?.risk_level)} />
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="h3" sx={{
                fontWeight: 900, color: riskColor(rs?.risk_level), lineHeight: 1,
                fontFamily: '"Barlow Condensed", sans-serif',
              }}>
                {rs?.risk_percentage > 0 ? '+' : ''}{rs?.risk_percentage?.toFixed(1)}%
              </Typography>
              <Chip
                label={(rs?.risk_level || '').toUpperCase().replace('_', ' ')} size="small"
                sx={{ mt: 0.5, bgcolor: `${riskColor(rs?.risk_level)}18`, color: riskColor(rs?.risk_level), fontWeight: 700 }}
              />
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                {rs?.risk_label}
              </Typography>
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            <KV label="Total Savings"    value={fmt(rs?.total_savings)}       valueColor={P.green} />
            <KV label="Long-term Loans"  value={fmt(rs?.total_loans)}         valueColor={P.amber} />
            <KV label="Advance Loans"    value={fmt(rs?.total_advance_loans)} valueColor={P.amber} />
            <KV label="Total Exposure"   value={fmt(rs?.total_exposure)}      valueColor={P.red} />
          </Paper>
        </Grid>

        {/* Loan Portfolio */}
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <SectionTitle icon={LoanIcon} label="Loan Portfolio" color={P.blue} />
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
              {[
                { label: 'Active Loans', count: lp?.members_with_active_loans, color: P.blue },
                { label: 'No Loan',      count: lp?.members_without_loans,    color: P.muted },
              ].map(({ label, count, color }) => (
                <Box key={label} sx={{
                  px: 2, py: 1, borderRadius: 1.5,
                  bgcolor: `${color}10`, border: `1px solid ${color}25`, textAlign: 'center',
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color, lineHeight: 1 }}>{count}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
                </Box>
              ))}
            </Box>
            {[
              { key: 'nearly_complete', color: P.green, icon: '✅' },
              { key: 'halfway',         color: P.teal,  icon: '⏳' },
              { key: 'just_started',    color: P.amber, icon: '🆕' },
            ].map(({ key, color, icon }) => {
              const stage = lp?.loan_stages?.[key];
              if (!stage) return null;
              return (
                <Box key={key} sx={{
                  mb: 1.2, p: 1.2, borderRadius: 1.5,
                  bgcolor: `${color}08`, border: `1px solid ${color}25`,
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{icon} {stage.description}</Typography>
                    <Chip label={stage.count} size="small" sx={{
                      bgcolor: `${color}20`, color, fontWeight: 800, minWidth: 32,
                    }} />
                  </Box>
                </Box>
              );
            })}
          </Paper>
        </Grid>

        {/* ── Group Rating History — proper grid, not horizontal scroll ── */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
              <SectionTitle icon={HistoryIcon} label="Group Rating History" color={P.purple} />
              {rh?.has_history && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {rh?.trend === 'improving' && <TrendingUpIcon sx={{ color: P.green, fontSize: 18 }} />}
                  {rh?.trend === 'declining' && <TrendingDownIcon sx={{ color: P.red,   fontSize: 18 }} />}
                  {rh?.trend === 'stable'    && <TrendingFlatIcon sx={{ color: P.teal,  fontSize: 18 }} />}
                  <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 340 }}>
                    {rh?.trend_summary}
                  </Typography>
                </Box>
              )}
            </Box>

            {!rh?.has_history ? (
              <Alert severity="info">No group rating history available from November 2025 onwards.</Alert>
            ) : (
              // Grid: up to 3 per row on desktop, 2 on tablet, 1 on mobile
              // Each card is roughly square — scores on left, commentary on right
              <Grid container spacing={2}>
                {rh.rating_history.map((rating, i) => {
                  const isLatest = i === 0;
                  const gc = rating.commentary;
                  return (
                    <Grid item xs={12} sm={6} lg={4} key={i}>
                      <Box sx={{
                        borderRadius: 2, overflow: 'hidden',
                        border: `${isLatest ? 2 : 1}px solid ${isLatest ? P.purple : 'divider'}`,
                        height: '100%', display: 'flex', flexDirection: 'column',
                      }}>

                        {/* Card header: meeting date + grade badge */}
                        <Box sx={{
                          px: 2, py: 1.5,
                          bgcolor: isLatest ? `${P.purple}10` : 'background.paper',
                          borderBottom: 1, borderColor: 'divider',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                          <Box>
                            {isLatest && (
                              <Chip label="Latest" size="small" sx={{
                                mb: 0.5, bgcolor: `${P.purple}18`, color: P.purple,
                                fontWeight: 700, fontSize: '0.65rem', display: 'block',
                              }} />
                            )}
                            {/* Only meeting date, not the range */}
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {fmtDate(rating.period_end)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Meeting date</Typography>
                          </Box>
                          <GradeBadge grade={rating.grade} score={rating.total_score} />
                        </Box>

                        {/* Score bars + commentary side by side on wider screens */}
                        <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

                          {/* Score bars — percentage */}
                          <Box>
                            <ScoreBar
                              label="Loan Repayment"
                              score={rating.scores.loan_repayment.score} max={35}
                              color={gradeColor(rating.grade)}
                            />
                            <ScoreBar
                              label="Advance Interest"
                              score={rating.scores.advance_interest.score} max={25}
                              color={P.teal}
                            />
                            <ScoreBar
                              label="Contribution"
                              score={rating.scores.contribution.score} max={15}
                              color={P.blue}
                            />
                            <ScoreBar
                              label="Defaulters"
                              score={rating.scores.defaulters.score} max={10}
                              color={rating.scores.defaulters.score < 5 ? P.red : P.green}
                            />
                          </Box>

                          {/* Arrears warning */}
                          {(rating.loan_period_arrears > 0 || rating.advance_period_arrears > 0) && (
                            <Box sx={{
                              p: 1, borderRadius: 1,
                              bgcolor: `${P.amber}10`, border: `1px solid ${P.amber}30`,
                            }}>
                              {rating.loan_period_arrears > 0 && (
                                <Typography variant="caption" sx={{ color: P.amber, display: 'block', fontSize: '0.68rem' }}>
                                  ⚠ Loan arrears: {fmt(rating.loan_period_arrears)}
                                </Typography>
                              )}
                              {rating.advance_period_arrears > 0 && (
                                <Typography variant="caption" sx={{ color: P.amber, display: 'block', fontSize: '0.68rem' }}>
                                  ⚠ Advance arrears: {fmt(rating.advance_period_arrears)}
                                </Typography>
                              )}
                            </Box>
                          )}

                          {/* Commentary — coloured left-border bullets */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                            {[
                              { text: gc?.loan_repayment,  color: gradeColor(rating.grade) },
                              { text: gc?.advance_interest, color: P.teal },
                              { text: gc?.defaulters,       color: rating.scores.defaulters.score < 5 ? P.red : P.muted },
                            ].map((c, ci) => c.text && (
                              <Box key={ci} sx={{ display: 'flex', gap: 0.8 }}>
                                <Box sx={{ width: 3, minWidth: 3, borderRadius: 2, bgcolor: c.color, mt: 0.4, flexShrink: 0 }} />
                                <Typography variant="caption" sx={{
                                  color: 'text.secondary', fontSize: '0.68rem', lineHeight: 1.5,
                                }}>
                                  {c.text}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Paper>
        </Grid>

      </Grid>
    </Box>
  );
};

// ─── Main Dialog ──────────────────────────────────────────────────────────────
const LoanApprovalAssessmentDialog = ({ open, loan, onClose, onApprove, loading: approveLoading }) => {
  const [tab, setTab]                 = useState(0);
  const [assessment, setAssessment]   = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError]   = useState(null);

  useEffect(() => {
    if (!open || !loan) return;
    setTab(0);
    setAssessment(null);
    setFetchError(null);
    setFetchLoading(true);

    fetchLoanApprovalAssessment(loan.database_id)
      .then(data => setAssessment(data))
      .catch(err => {
        console.error('Assessment fetch error:', err);
        setFetchError(err.response?.data?.message || 'Failed to load assessment data.');
      })
      .finally(() => setFetchLoading(false));
  }, [open, loan]);

  const handleApprove = () => {
    if (onApprove && loan) onApprove(loan, '');
  };

  return (
    <Dialog
      open={open}
      onClose={() => !approveLoading && onClose()}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: '85vh', maxHeight: '92vh',
          display: 'flex', flexDirection: 'column',
          fontFamily: '"DM Sans", sans-serif',
          overflow: 'hidden',
        },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${P.navy} 0%, ${P.blue} 100%)`,
        px: 3, pt: 1.25, pb: 1.25, color: 'white', flexShrink: 0,
      }}>
        {/* Title row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography sx={{
              fontWeight: 800, lineHeight: 1.2, fontSize: '1.1rem',
              fontFamily: '"Barlow Condensed", sans-serif',
              letterSpacing: '0.01em',
            }}>
              {loan ? (loan.member?.name || loan.memberName) : 'Assessment'}
              {loan && (
                <Box component="span" sx={{
                  fontWeight: 400, fontSize: '0.78rem', opacity: 0.6, ml: 1,
                  fontFamily: '"DM Sans", sans-serif',
                }}>
                  · Assessment for Loan #{loan.id}
                </Box>
              )}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: 'white', opacity: 0.7, '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.15)' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Pill-style tab switcher */}
        <Box sx={{
          display: 'flex', gap: 0.5,
          bgcolor: 'rgba(0,0,0,0.22)',
          borderRadius: '10px', p: '4px',
          width: 'fit-content',
        }}>
          {['Member Overview', 'Member Performance History', 'Member Repayment History', 'Member Group Summary'].map((label, i) => (
            <Box
              key={label}
              onClick={() => setTab(i)}
              sx={{
                px: 1.6, py: 0.55,
                borderRadius: '7px',
                cursor: 'pointer',
                fontSize: '0.74rem',
                fontWeight: tab === i ? 700 : 500,
                letterSpacing: '0.02em',
                color: tab === i ? P.navy : 'rgba(255,255,255,0.72)',
                bgcolor: tab === i ? 'white' : 'transparent',
                boxShadow: tab === i ? '0 1px 4px rgba(0,0,0,0.18)' : 'none',
                transition: 'all 0.15s ease',
                userSelect: 'none',
                whiteSpace: 'nowrap',
                '&:hover': tab !== i ? {
                  bgcolor: 'rgba(255,255,255,0.14)',
                  color: 'white',
                } : {},
              }}
            >
              {label}
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Content ── */}
      <DialogContent sx={{ flex: 1, overflowY: 'auto', p: 3, bgcolor: 'background.default' }}>
        {fetchLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 2 }}>
            <CircularProgress sx={{ color: P.blue }} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading assessment data…</Typography>
          </Box>
        )}

        {fetchError && !fetchLoading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {fetchError}
            <br />
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              You can still approve the loan, but review the data manually.
            </Typography>
          </Alert>
        )}

        {assessment && !fetchLoading && (
          <>
            <TabPanel value={tab} index={0}><OverviewTab     data={assessment} /></TabPanel>
            <TabPanel value={tab} index={1}><PerformanceTab  data={assessment} /></TabPanel>
            <TabPanel value={tab} index={2}><RepaymentTab    data={assessment} /></TabPanel>
            <TabPanel value={tab} index={3}><GroupTab        data={assessment} /></TabPanel>
          </>
        )}
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions sx={{
        px: 3, py: 2, borderTop: 1, borderColor: 'divider',
        bgcolor: 'background.default', gap: 1.5, flexShrink: 0,
      }}>
        <Button
          onClick={onClose}
          disabled={approveLoading}
          variant="outlined"
          sx={{ borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: 'text.primary' } }}
        >
          Close
        </Button>
        <Button
          onClick={handleApprove}
          disabled={approveLoading || !loan}
          variant="contained"
          startIcon={
            approveLoading
              ? <CircularProgress size={16} sx={{ color: 'white' }} />
              : <CheckIcon />
          }
          sx={{
            bgcolor: P.green, fontWeight: 700, px: 3,
            '&:hover':    { bgcolor: '#15803d' },
            '&:disabled': { bgcolor: `${P.green}60` },
          }}
        >
          {approveLoading ? 'Approving…' : 'Approve Loan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoanApprovalAssessmentDialog;